"use client";

import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { DEFAULT_GASTIFY_MESSAGES } from "./chat-messages";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Typing() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="flex items-end gap-1.5"
    >
      <Avatar />
      <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm border border-slate-200/80 bg-white px-3 py-2.5 shadow-xs">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="h-1.5 w-1.5 rounded-full bg-emerald-500"
            animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
            transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
          />
        ))}
      </div>
    </motion.div>
  );
}

function Avatar() {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-emerald-500/30 bg-slate-900">
      <img src="/gastify-icon-192.png" alt="" className="h-full w-full object-contain p-0.5" />
    </div>
  );
}

function Bubble({ message }) {
  const isUser = message.sender === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`flex w-full items-end gap-1.5 ${isUser ? "justify-end" : "justify-start"}`}
    >
      {!isUser && <Avatar />}
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-2 text-[10.5px] leading-snug ${
          isUser
            ? "rounded-br-sm bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-[0_6px_16px_-4px_rgba(16,185,129,0.4)]"
            : "rounded-bl-sm border border-slate-200/80 bg-white text-slate-800 shadow-xs"
        }`}
      >
        {message.content}
      </div>
    </motion.div>
  );
}

/**
 * Tela de chat somente leitura, feita para ficar dentro do mockup de celular.
 * Toca a conversa sozinha, em loop. Não tem campo de digitação de propósito.
 */
export function PhoneChatScreen({
  messages = DEFAULT_GASTIFY_MESSAGES,
  typingMs = 1100,
  gapMs = 1500,
  restartMs = 5000,
  heightClass = "h-[470px]",
}) {
  const [count, setCount] = useState(0);
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setCount(messages.length);
      return undefined;
    }

    let cancelled = false;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));

    (async () => {
      await wait(600);
      while (!cancelled) {
        setCount(0);
        for (let i = 0; i < messages.length && !cancelled; i += 1) {
          if (messages[i].sender === "assistant") {
            setTyping(true);
            await wait(typingMs);
            if (cancelled) return;
            setTyping(false);
          }
          setCount(i + 1);
          await wait(gapMs);
        }
        await wait(restartMs);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [messages, typingMs, gapMs, restartMs]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [count, typing]);

  return (
    <div className="flex flex-col bg-[#F8FAFC] text-slate-800">
      {/* Cabeçalho do app */}
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-slate-200/80 bg-white px-3 py-2">
        <div className="relative flex h-8 w-8 items-center justify-center rounded-xl border border-emerald-500/40 bg-slate-950 p-1">
          <img src="/gastify-icon-192.png" alt="" className="h-full w-full object-contain" />
          <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border-2 border-white bg-emerald-500" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-[11px] font-extrabold leading-none text-slate-900">Gastify IA</p>
            <span className="inline-flex items-center gap-0.5 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[7px] font-black text-emerald-700">
              <Sparkles size={7} /> Online
            </span>
          </div>
          <p className="mt-0.5 text-[8px] font-medium leading-none text-slate-400">
            Assistente Financeiro Consciente
          </p>
        </div>
      </div>

      {/* Mensagens: sem rolagem manual, a página rola sozinha para a última */}
      <div
        ref={scrollRef}
        role="log"
        aria-label="Exemplo de conversa com o assistente do Gastify"
        className={`${heightClass} space-y-2.5 overflow-hidden px-3 py-3`}
      >
        {messages.slice(0, count).map((m) => (
          <Bubble key={m.id} message={m} />
        ))}
        <AnimatePresence>{typing && <Typing />}</AnimatePresence>
      </div>

      <div className="flex flex-shrink-0 justify-center pb-1.5 pt-1">
        <span className="h-1 w-20 rounded-full bg-slate-300" />
      </div>
    </div>
  );
}

export default PhoneChatScreen;
